import { useCallback, useEffect, useRef } from "react";

/**
 * Enables click-and-drag paint selection on a grid of toggle buttons.
 * The first clicked item sets the mode (select / deselect) for the drag session.
 */
export default function usePaintToggleGrid({ onApply, isBlocked }) {
   const dragRef = useRef({ active: false, mode: null });

   const endDrag = useCallback(() => {
      dragRef.current = { active: false, mode: null };
   }, []);

   useEffect(() => {
      window.addEventListener("mouseup", endDrag);
      return () => window.removeEventListener("mouseup", endDrag);
   }, [endDrag]);

   const getItemHandlers = useCallback(
      (id, isActive) => ({
         onMouseDown: (event) => {
            if (event.button !== 0 || isBlocked?.(id)) return;
            event.preventDefault();
            const mode = isActive ? "deselect" : "select";
            dragRef.current = { active: true, mode };
            onApply(id, mode);
         },
         onClick: (event) => {
            // mousedown already toggled; suppress the follow-up click (double-toggle).
            event.preventDefault();
         },
         onMouseEnter: () => {
            const drag = dragRef.current;
            if (!drag.active || isBlocked?.(id)) return;
            const shouldBeActive = drag.mode === "select";
            if (shouldBeActive !== isActive) {
               onApply(id, drag.mode);
            }
         },
      }),
      [isBlocked, onApply],
   );

   return { getItemHandlers, endDrag };
}
